import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { QRCodeSVG } from 'qrcode.react';
import { 
  IconSearch, 
  IconFileDescription,
  IconEye, 
  IconEyeOff,
  IconEdit, 
  IconTrash, 
  IconQrcode, 
  IconPlus,
  IconX,
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconDownload
} from '@tabler/icons-react';
import { getMarketingPages, deleteMarketingPage, toggleMarketingPageVisibility, getImageUrl, fetchCampaigns, fetchCampaignVouchers, fetchPublishedVouchers } from '../services/api';
import { isVoucherListItemDisplayable } from '../utils/voucherExpiry';
import './MarketingPages.css';

// Format date/time using user's browser timezone
const formatDateTime = (isoString) => {
  if (!isoString) return '-';
  try {
    return new Date(isoString).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).replace(',', '');
  } catch (error) {
    return '-';
  }
};

/**
 * Copy to clipboard when Permissions-Policy blocks navigator.clipboard (e.g. embedded dev tools / iframes).
 */
async function copyTextToClipboard(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through */
    }
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '0';
    document.body.appendChild(textarea);
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

// Mock voucher data (should match CreateMarketingPage.js)
const AVAILABLE_VOUCHERS = [
  { id: 1, name: 'Starbucks Weekend BIF1 Deals', type: 'Product Voucher', price: 9.9, settlementPrice: 0, quantity: 100, validPeriod: '06/12/2024 - 24/12/2024', country: 'Hong Kong' },
  { id: 2, name: 'Genting Theme Park 50% - School Holiday', type: 'Product Voucher', price: 9.9, settlementPrice: 0, quantity: 100, validPeriod: '06/12/2024 - 24/12/2024', country: 'Malaysia' },
  { id: 3, name: 'Genting Theme Park 50% - School Holiday', type: 'Product Voucher', price: 9.9, settlementPrice: 0, quantity: 100, validPeriod: '06/12/2024 - 24/12/2024', country: 'Malaysia' },
  { id: 4, name: 'Starbucks Weekend BIF1 Deals', type: 'Product Voucher', price: 9.9, settlementPrice: 0, quantity: 100, validPeriod: '06/12/2024 - 24/12/2024', country: 'Malaysia' },
];

function MarketingPages() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [marketingPages, setMarketingPages] = useState([]);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [viewDetailsId, setViewDetailsId] = useState(null);
  const [viewDetailsData, setViewDetailsData] = useState(null);
  const [loadedImages, setLoadedImages] = useState({}); // Images loaded from IndexedDB
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [prevBannerIndex, setPrevBannerIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState('next');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [qrCodeId, setQrCodeId] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [showCampaignVouchersModal, setShowCampaignVouchersModal] = useState(false);
  const [campaignVouchers, setCampaignVouchers] = useState([]);
  const [campaignVouchersLoading, setCampaignVouchersLoading] = useState(false);
  const [selectedCampaignForVouchers, setSelectedCampaignForVouchers] = useState(null);
  const [apiVouchers, setApiVouchers] = useState([]); // Vouchers from API for banner link display
  const [filteredVoucherCount, setFilteredVoucherCount] = useState({}); // Filtered voucher count from API

  // Load vouchers from API for banner link display
  const loadVouchers = useCallback(async () => {
    try {
      const response = await fetchPublishedVouchers(1, 100);
      if (response?.message?.data) {
        const rows = response.message.data.filter(isVoucherListItemDisplayable);
        const vouchers = rows.map(item => ({
          id: item.campaignVoucherSchemaId || item._id || item.id,
          name: item.voucherSchema?.voucherSchemaName?.en || item.name || 'Unknown Voucher'
        }));
        setApiVouchers(vouchers);
      }
    } catch (error) {
      console.error('Failed to load vouchers:', error);
    }
  }, []);

  // Load campaigns from API
  const loadCampaigns = useCallback(async () => {
    try {
      const response = await fetchCampaigns(1, 100);
      if (response?.status === 'Success' && response.message?.data) {
        setCampaigns(response.message.data.map(c => ({
          id: c.campaignId,
          name: c.name,
          publishedVoucherCount: c.publishedVoucherCount || 0
        })));
      }
    } catch (error) {
      console.error('Error loading campaigns:', error);
    }
  }, []);

  // Get campaign name by ID
  const getCampaignName = useCallback((campaignId) => {
    if (!campaignId || campaignId === '-') return '-';
    const campaign = campaigns.find(c => c.id === campaignId);
    return campaign?.name || campaignId;
  }, [campaigns]);

  // Load campaign vouchers for modal
  const loadCampaignVouchers = async (campaignId, updateCountOnly = false) => {
    if (!campaignId) return;
    
    if (!updateCountOnly) {
      setCampaignVouchersLoading(true);
      setCampaignVouchers([]);
    }
    try {
      const response = await fetchCampaignVouchers(campaignId, 1, 100000);
      
      if (response?.status === 'Success' && response.message?.data) {
        const sourceRows = (response.message.data || []).filter(isVoucherListItemDisplayable);
        const vouchers = sourceRows.map(item => {
          const vs = item.voucherSchema || {};
          const token = item.token || {};
          const startDate = vs.expiryStartDatetime ? new Date(vs.expiryStartDatetime).toLocaleDateString('en-GB') : '';
          const endDate = vs.expiryEndDatetime ? new Date(vs.expiryEndDatetime).toLocaleDateString('en-GB') : '';
          const validPeriod = startDate && endDate ? `${startDate} - ${endDate}` : '-';
          
          // Get brands (can have multiple) - check both item.brandList and vs.brands
          const brands = (item.brandList || []).map(b => b.brandName).join(', ') || 
                        (vs.brands || []).map(b => b.brandName).join(', ') || '-';
          
          // Get categories (can have multiple) - check both item.categoryList and vs.categories
          const categories = (item.categoryList || []).map(c => c.categoryName).join(', ') || 
                            (vs.categories || []).map(c => c.categoryName).join(', ') || '-';
          
          // Get image URL
          const imageUrl = vs.image?.imageUrl || '';
          
          // Get token info
          const tokenSymbol = token.symbol || 'Token';
          const tokenIcon = token.logo?.imageUrl || '';
          
          return {
            id: item.campaignVoucherSchemaId,
            name: vs.voucherSchemaName?.en || '-',
            type: vs.voucherType || '-',
            price: item.price || 0,
            brand: brands,
            quantity: item.quantity != null ? item.quantity : 'Infinite',
            validPeriod: validPeriod,
            category: categories,
            country: vs.country?.countryName || '-',
            imageUrl: imageUrl,
            tokenSymbol: tokenSymbol,
            tokenIcon: tokenIcon
          };
        });
        
        // Update filtered voucher count for this campaign
        setFilteredVoucherCount(prev => ({
          ...prev,
          [campaignId]: vouchers.length
        }));
        
        if (!updateCountOnly) {
          setCampaignVouchers(vouchers);
        }
      } else {
        // No vouchers found - set count to 0
        setFilteredVoucherCount(prev => ({
          ...prev,
          [campaignId]: 0
        }));
      }
    } catch (error) {
      console.error('❌ Failed to fetch campaign vouchers:', error);
      if (!updateCountOnly) {
        toast.error('Failed to load campaign vouchers');
      }
    } finally {
      if (!updateCountOnly) {
        setCampaignVouchersLoading(false);
      }
    }
  };

  // Open campaign vouchers modal
  const handleShowCampaignVouchers = (campaignId) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    setSelectedCampaignForVouchers(campaign);
    setShowCampaignVouchersModal(true);
    loadCampaignVouchers(campaignId);
  };

  // Load pages from API
  const loadPages = useCallback(async () => {
    try {
      const pages = await getMarketingPages();
      setMarketingPages(pages); // Already sorted by newest first from API
    } catch (error) {
      console.error('Error loading marketing pages:', error);
      // Fallback to empty array if API fails
      setMarketingPages([]);
    }
  }, []);

  useEffect(() => {
    loadPages();
    loadCampaigns();
  }, [loadPages, loadCampaigns]);

  // Auto-advance carousel
  useEffect(() => {
    if (viewDetailsData && viewDetailsData.fullConfig.banners && viewDetailsData.fullConfig.banners.length > 1 && !isTransitioning) {
      const interval = setInterval(() => {
        setSlideDirection('next');
        setPrevBannerIndex(currentBannerIndex);
        setIsTransitioning(true);
        setCurrentBannerIndex((prev) => {
          const banners = viewDetailsData.fullConfig.banners;
          return prev === banners.length - 1 ? 0 : prev + 1;
        });
        setTimeout(() => setIsTransitioning(false), 500);
      }, 3000); // Change image every 3 seconds

      return () => clearInterval(interval);
    }
  }, [viewDetailsData, currentBannerIndex, isTransitioning]);

  const filteredPages = marketingPages.filter(page => 
    page.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewDetails = useCallback((id) => {
    const page = marketingPages.find(p => p.id === id);
    if (page) {
      setViewDetailsId(id);
      setViewDetailsData(page);
      setCurrentBannerIndex(0);
      
      // Build loaded images from server URLs
      const images = {};
      
      // Map banner images
      if (page.fullConfig?.banners) {
        page.fullConfig.banners.forEach(banner => {
          if (banner.imageUrl) {
            images[banner.id] = getImageUrl(banner.imageUrl);
          }
        });
        
        // Check if any banner has a voucher link - if so, load vouchers for display
        const hasBannerVoucherLink = page.fullConfig.banners.some(
          b => b.linkType === 'voucher' && b.selectedVoucher && !b.selectedVoucherName
        );
        if (hasBannerVoucherLink && apiVouchers.length === 0) {
          loadVouchers();
        }
      }
      
      // Map company logo
      if (page.fullConfig?.basic?.companyLogoUrl) {
        images['companyLogo'] = getImageUrl(page.fullConfig.basic.companyLogoUrl);
      }
      
      setLoadedImages(images);
      
      // Fetch filtered voucher count for linked campaign
      if (page.fullConfig?.content?.linkCampaignEnabled && page.fullConfig?.content?.selectedCampaign) {
        loadCampaignVouchers(page.fullConfig.content.selectedCampaign, true);
      }
    }
  }, [marketingPages, apiVouchers, loadVouchers]);

  const handleCloseViewDetails = () => {
    setViewDetailsId(null);
    setViewDetailsData(null);
    setCurrentBannerIndex(0);
  };

  const handlePreviousBanner = () => {
    if (viewDetailsData && viewDetailsData.fullConfig.banners && !isTransitioning) {
      const banners = viewDetailsData.fullConfig.banners;
      setSlideDirection('prev');
      setPrevBannerIndex(currentBannerIndex);
      setIsTransitioning(true);
      setCurrentBannerIndex((prev) => (prev === 0 ? banners.length - 1 : prev - 1));
      setTimeout(() => setIsTransitioning(false), 500);
    }
  };

  const handleNextBanner = () => {
    if (viewDetailsData && viewDetailsData.fullConfig.banners && !isTransitioning) {
      const banners = viewDetailsData.fullConfig.banners;
      setSlideDirection('next');
      setPrevBannerIndex(currentBannerIndex);
      setIsTransitioning(true);
      setCurrentBannerIndex((prev) => (prev === banners.length - 1 ? 0 : prev + 1));
      setTimeout(() => setIsTransitioning(false), 500);
    }
  };

  const handleEdit = (id) => {
    navigate(`/marketing/edit/${id}`);
  };

  const handleDelete = (id) => {
    setDeleteConfirmId(id);
  };

  const handleConfirmDelete = async () => {
    try {
      // Delete from API (backend handles image cleanup)
      await deleteMarketingPage(deleteConfirmId);
      
      // Update local state
      const updatedPages = marketingPages.filter(page => page.id !== deleteConfirmId);
      setMarketingPages(updatedPages);
      setDeleteConfirmId(null);
      toast.success('Marketing page deleted successfully!');
    } catch (error) {
      console.error('Error deleting marketing page:', error);
      toast.error('Failed to delete marketing page. Please try again.');
      setDeleteConfirmId(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmId(null);
  };

  const handleToggleVisibility = async (id) => {
    try {
      const result = await toggleMarketingPageVisibility(id);
      
      // Update local state
      setMarketingPages(prevPages => 
        prevPages.map(page => 
          page.id === id ? { ...page, hidden: result.hidden } : page
        )
      );
      
      toast.success(result.hidden ? 'Marketing page hidden successfully!' : 'Marketing page is now visible!');
    } catch (error) {
      console.error('Error toggling marketing page visibility:', error);
      toast.error('Failed to toggle visibility. Please try again.');
    }
  };

  const handleDownloadQR = (id) => {
    const page = marketingPages.find(p => p.id === id);
    if (page) {
      // Generate the marketing page URL pointing to the user-facing PWA
      // Priority: 1) Environment variable, 2) Use current origin (works if PWA is on same domain), 3) Fallback to localhost
      let pwaBaseUrl = process.env.REACT_APP_PWA_URL;
      
      if (!pwaBaseUrl) {
        // Use current origin - works in production if PWA and Ops are on same domain
        // In production, both frontends are typically on the same domain with different paths
        const currentOrigin = window.location.origin;
        
        // If we're in development (localhost), use localhost:3002 (PWA dev server)
        if (currentOrigin.includes('localhost')) {
          pwaBaseUrl = 'http://localhost:3002';
        } else {
          // In production, use the same origin (PWA should be accessible on same domain)
          pwaBaseUrl = currentOrigin;
        }
      }
      
      const marketingPageUrl = `${pwaBaseUrl}/marketing/${id}`;
      setQrCodeId(id);
      setQrCodeData({
        url: marketingPageUrl,
        name: page.name
      });
    }
  };

  const handleCloseQR = () => {
    setQrCodeId(null);
    setQrCodeData(null);
  };

  const handleCopyLink = async () => {
    if (!qrCodeData?.url) return;
    const ok = await copyTextToClipboard(qrCodeData.url);
    if (ok) {
      toast.success('Link copied to clipboard!');
    } else {
      window.prompt('Copy this link (select the text, then Cmd/Ctrl+C):', qrCodeData.url);
    }
  };

  const handleDownloadQRImage = () => {
    if (qrCodeData) {
      // Find the QR code SVG element
      const svgElement = document.querySelector('.qr-code-container svg');
      if (svgElement) {
        // Get SVG data
        const svgData = new XMLSerializer().serializeToString(svgElement);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        
        // Set canvas size
        canvas.width = 256;
        canvas.height = 256;
        
        img.onload = () => {
          ctx.drawImage(img, 0, 0);
          canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `qr-code-${qrCodeData.name.replace(/\s+/g, '-').toLowerCase()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success('QR code downloaded successfully!');
          });
        };
        
        img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
      }
    }
  };

  const handleCreateNew = () => {
    navigate('/marketing/create');
  };

  return (
    <div className="marketing-pages">
      <div className="page-header">
        <h1 className="page-title">Marketing Page</h1>
        <button className="btn-primary" onClick={handleCreateNew}>
          <IconPlus size={18} stroke={2.5} />
          Create New Marketing Page
        </button>
      </div>

      <div className="search-container">
        <div className="search-wrapper">
          <IconSearch className="search-icon" size={18} stroke={2} />
          <input
            type="text"
            className="search-input"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Marketing Page ID</th>
              <th>Marketing Page Name</th>
              <th>Created Date Time</th>
              <th>No. of Banner</th>
              <th>Link Voucher</th>
              <th>Link Campaign</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPages.length > 0 ? (
              filteredPages.map((page) => (
                <tr key={page.id} className={page.hidden ? 'row-hidden' : ''}>
                  <td className="id-cell">{page.id}</td>
                  <td className="name-cell">
                    <span>{page.name}</span>
                    {page.hidden && <span className="hidden-badge">Hidden</span>}
                  </td>
                  <td className="date-cell">
                    {formatDateTime(page.createdAt) || page.createdDateTime || '-'}
                  </td>
                  <td className="center-cell">{page.bannerCount}</td>
                  <td className="center-cell">{page.voucherCount}</td>
                  <td className="campaign-cell">
                    {(() => {
                      // Check if link campaign is enabled
                      if (!page.fullConfig?.content?.linkCampaignEnabled) {
                        return '-';
                      }
                      // First try stored campaign name
                      if (page.fullConfig?.content?.selectedCampaignName) {
                        return page.fullConfig.content.selectedCampaignName;
                      }
                      // Then try to look up from campaigns using selectedCampaign or campaignLink
                      const campaignId = page.fullConfig?.content?.selectedCampaign || page.campaignLink;
                      if (campaignId && campaignId !== '-') {
                        const campaign = campaigns.find(c => c.id === campaignId);
                        if (campaign?.name) {
                          return campaign.name;
                        }
                        // If campaign not found in list, show the ID
                        return campaignId;
                      }
                      // If no campaign linked, show dash
                      return '-';
                    })()}
                  </td>
                  <td className="actions-cell">
                    <button 
                      className="action-btn"
                      onClick={() => handleViewDetails(page.id)}
                      title="View Details"
                    >
                      <IconFileDescription size={16} stroke={1.5} />
                    </button>
                    <button 
                      className="action-btn"
                      onClick={() => handleEdit(page.id)}
                      title="Edit"
                    >
                      <IconEdit size={16} stroke={1.5} />
                    </button>
                    <button 
                      className="action-btn"
                      onClick={() => handleDelete(page.id)}
                      title="Delete"
                    >
                      <IconTrash size={16} stroke={1.5} />
                    </button>
                    <button 
                      className="action-btn"
                      onClick={() => handleDownloadQR(page.id)}
                      title="Download QR"
                    >
                      <IconQrcode size={16} stroke={1.5} />
                    </button>
                    <button 
                      className={`action-btn ${page.hidden ? 'hidden-active' : ''}`}
                      onClick={() => handleToggleVisibility(page.id)}
                      title={page.hidden ? 'Unhide Page' : 'Hide Page'}
                    >
                      {page.hidden ? <IconEyeOff size={16} stroke={1.5} /> : <IconEye size={16} stroke={1.5} />}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="empty-state">
                  <div className="empty-content">
                    <img src={`${process.env.PUBLIC_URL}/empty.svg`} alt="No marketing pages" className="empty-image" />
                    <h3 className="empty-title">No Marketing Page</h3>
                    <span className="empty-link" onClick={handleCreateNew}>Create new marketing page</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="modal-overlay" onClick={handleCancelDelete}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Marketing Page</h3>
              <button className="modal-close" onClick={handleCancelDelete}>
                <IconX size={20} stroke={2} />
              </button>
            </div>
            <div className="modal-body">
              <p className="confirm-text">Are you sure you want to delete this marketing page?</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCancelDelete}>
                Cancel
              </button>
              <button className="btn-danger" onClick={handleConfirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {viewDetailsId && viewDetailsData && viewDetailsData.fullConfig && (
        <div className="modal-overlay" onClick={handleCloseViewDetails}>
          <div className="modal-content modal-view-details" onClick={(e) => e.stopPropagation()}>
            <div className="modal-close-wrapper">
              <button className="modal-close" onClick={handleCloseViewDetails}>
                <IconX size={20} stroke={2} />
              </button>
            </div>
            <div className="modal-header">
              <div>
                <h3 className="modal-title">{viewDetailsData.name}</h3>
                {viewDetailsData.fullConfig.basic.description && (
                  <p className="modal-description">{viewDetailsData.fullConfig.basic.description}</p>
                )}
                <p className="modal-subtitle">
                  {formatDateTime(viewDetailsData.createdAt) || viewDetailsData.createdDateTime || '-'}
                </p>
              </div>
            </div>
            <div className="modal-body view-details-body">
              {/* Banner Image Carousel */}
              {viewDetailsData.fullConfig.banners && viewDetailsData.fullConfig.banners.length > 0 && (
                <div className="banner-carousel-container">
                  <div className="banner-carousel">
                    <div className="carousel-nav-area carousel-nav-left">
                      <button 
                        className="carousel-nav carousel-prev" 
                        onClick={handlePreviousBanner}
                        disabled={viewDetailsData.fullConfig.banners.length <= 1}
                      >
                        <IconChevronLeft size={24} stroke={2} />
                      </button>
                    </div>
                    <div className="banner-carousel-image-wrapper">
                      <div className="banner-carousel-image">
                        {(() => {
                          const banners = viewDetailsData.fullConfig.banners;
                          const currentBanner = banners[currentBannerIndex];
                          const prevBanner = isTransitioning ? banners[prevBannerIndex] : null;
                          // Get images from loaded state (using banner.id as key)
                          const currentImageData = currentBanner?.id ? loadedImages[currentBanner.id] : null;
                          const currentImageName = currentBanner?.image;
                          const prevImageData = prevBanner?.id ? loadedImages[prevBanner.id] : null;
                          
                          return (
                            <>
                              {isTransitioning && prevImageData && (
                                <img 
                                  key={`prev-${prevBannerIndex}`}
                                  src={prevImageData} 
                                  alt={prevBanner?.title || 'Banner image'}
                                  className={`banner-carousel-img slide-exit-${slideDirection === 'next' ? 'next' : 'prev'}`}
                                />
                              )}
                              {currentImageData ? (
                                <img 
                                  key={`current-${currentBannerIndex}`}
                                  src={currentImageData} 
                                  alt={currentBanner?.title || 'Banner image'}
                                  className={`banner-carousel-img slide-${slideDirection}`}
                                />
                              ) : (
                                <div className="banner-image-placeholder">
                                  <span>Banner image preview</span>
                                  <span className="banner-image-name">
                                    {currentImageName || 'No image'}
                                  </span>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="carousel-nav-area carousel-nav-right">
                      <button 
                        className="carousel-nav carousel-next" 
                        onClick={handleNextBanner}
                        disabled={viewDetailsData.fullConfig.banners.length <= 1}
                      >
                        <IconChevronRight size={24} stroke={2} />
                      </button>
                    </div>
                  </div>
                  <div className="banner-carousel-indicator">
                    <div className="banner-indicator-name">
                      {viewDetailsData.fullConfig.banners[currentBannerIndex]?.title || 'Banner'}
                    </div>
                    <div className="banner-indicator-count">
                      displaying {currentBannerIndex + 1} of {viewDetailsData.fullConfig.banners.length}
                    </div>
                  </div>
                </div>
              )}

              {/* Banner Details */}
              <div className="review-section">
                <h3 className="review-section-title">Banner Details</h3>
                {viewDetailsData.fullConfig.banners && viewDetailsData.fullConfig.banners.length > 0 && (
                  <div className="review-table">
                    <div className="review-table-header review-banner-header">
                      <span>Order</span>
                      <span>Banner title</span>
                      <span>Banner image</span>
                      <span>Banner link</span>
                    </div>
                    {viewDetailsData.fullConfig.banners.map((banner, index) => {
                      const getLinkDisplay = () => {
                        if (banner.linkType === 'voucher' && banner.selectedVoucher) {
                          // Try stored name first, then API vouchers, then show dash
                          if (banner.selectedVoucherName) {
                            return banner.selectedVoucherName;
                          }
                          // Look up from API vouchers
                          const voucher = apiVouchers.find(v => v.id === banner.selectedVoucher);
                          return voucher?.name || '-';
                        }
                        return banner.link || '-';
                      };
                      
                      // Get image source for thumbnail
                      const getBannerImageSrc = () => {
                        if (banner.imageUrl) {
                          return getImageUrl(banner.imageUrl);
                        }
                        return null;
                      };
                      
                      const bannerImageSrc = getBannerImageSrc();
                      
                      return (
                        <div key={index} className="review-table-row review-banner-row">
                          <span>{index + 1}</span>
                          <span>{banner.title}</span>
                          <span>
                            {bannerImageSrc ? (
                              <div className="banner-table-thumbnail-wrapper">
                                <img 
                                  src={bannerImageSrc} 
                                  alt={banner.image || 'Banner'} 
                                  className="banner-table-thumbnail"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    if (e.target.nextSibling) {
                                      e.target.nextSibling.style.display = 'block';
                                    }
                                  }}
                                />
                                <span className="banner-table-filename">{banner.image || '-'}</span>
                              </div>
                            ) : (
                              <span>{banner.image || '-'}</span>
                            )}
                          </span>
                          <span>{getLinkDisplay()}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className="review-grid" style={{ marginTop: '1rem' }}>
                  <div className="review-item">
                    <span className="review-label">Slogan</span>
                    <span className="review-value">{viewDetailsData.fullConfig.basic.slogan || '-'}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Company Logo</span>
                    <div className="review-value company-logo-display">
                      {loadedImages['companyLogo'] ? (
                        <>
                          <img 
                            src={loadedImages['companyLogo']} 
                            alt="Company Logo" 
                            className="company-logo-thumbnail"
                          />
                          <span className="company-logo-name">{viewDetailsData.fullConfig.basic.companyLogoName || '-'}</span>
                        </>
                      ) : (
                        <span>{viewDetailsData.fullConfig.basic.companyLogoName || '-'}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Configuration */}
              <div className="review-section">
                <h3 className="review-section-title">Content Configuration</h3>
                
                {/* Link Voucher */}
                <div className="review-subsection">
                  <h4 className="review-subsection-title">Link Voucher</h4>
                  {viewDetailsData.fullConfig.content.linkVoucherEnabled ? (
                    <>
                      <div className="review-grid">
                        <div className="review-item">
                          <span className="review-label">Voucher section title</span>
                          <span className="review-value">{viewDetailsData.fullConfig.content.linkVoucherTitle}</span>
                        </div>
                        <div className="review-item">
                          <span className="review-label">Number of vouchers</span>
                          <span className="review-value">{viewDetailsData.fullConfig.content.vouchers.length}</span>
                        </div>
                      </div>
                      {viewDetailsData.fullConfig.content.vouchers.length > 0 && (
                        <div className="review-table review-table-wide">
                          <div className="review-table-header review-voucher-header">
                            <span>Voucher</span>
                            <span>Voucher Type</span>
                            <span>Price</span>
                            <span>Brand</span>
                            <span>Available Quantity</span>
                            <span>Validity Period</span>
                            <span>Category</span>
                            <span>Country</span>
                          </div>
                          {viewDetailsData.fullConfig.content.vouchers.map((voucher, index) => (
                            <div key={index} className="review-table-row review-voucher-row">
                              <span>{voucher.name || '-'}</span>
                              <span>{voucher.type || '-'}</span>
                              <span>{voucher.price || '-'}</span>
                              <span>{voucher.brand || '-'}</span>
                              <span>{voucher.quantity || '-'}</span>
                              <span>{voucher.validPeriod || '-'}</span>
                              <span>{voucher.category || '-'}</span>
                              <span>{voucher.country || '-'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="review-value">-</span>
                  )}
                </div>

                {/* Link Campaign */}
                <div className="review-subsection">
                  <h4 className="review-subsection-title">Link Campaign</h4>
                  {viewDetailsData.fullConfig.content.linkCampaignEnabled ? (
                    <div className="review-grid">
                      <div className="review-item">
                        <span className="review-label">Campaign section title</span>
                        <span className="review-value">{viewDetailsData.fullConfig.content.linkCampaignTitle}</span>
                      </div>
                      <div className="review-item">
                        <span className="review-label">Selected campaign</span>
                        <div className="review-value">
                          <div>
                            {viewDetailsData.fullConfig.content.selectedCampaignName || 
                             getCampaignName(viewDetailsData.fullConfig.content.selectedCampaign) || 
                             viewDetailsData.fullConfig.content.selectedCampaign || 
                             '-'}
                          </div>
                          {(() => {
                            const campaignId = viewDetailsData.fullConfig.content.selectedCampaign;
                            const campaign = campaigns.find(c => c.id === campaignId);
                            const voucherCount = filteredVoucherCount[campaignId] ?? campaign?.publishedVoucherCount ?? 0;
                            return (
                              <div 
                                style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.25rem', cursor: 'pointer', textDecoration: 'underline' }}
                                onClick={() => handleShowCampaignVouchers(campaignId)}
                              >
                                {voucherCount} {voucherCount === 1 ? 'voucher' : 'vouchers'}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <span className="review-value">-</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {qrCodeId && qrCodeData && (
        <div className="modal-overlay" onClick={handleCloseQR}>
          <div className="modal-content modal-qr" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">QR Code</h3>
              <button className="modal-close" onClick={handleCloseQR}>
                <IconX size={20} stroke={2} />
              </button>
            </div>
            <div className="modal-body qr-modal-body">
              <div className="qr-code-container">
                <QRCodeSVG
                  value={qrCodeData.url}
                  size={256}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <button className="qr-download-btn" onClick={handleDownloadQRImage}>
                <IconDownload size={18} stroke={2} />
                Download QR Code
              </button>
              <div className="qr-link-container">
                <div className="qr-link-label">Marketing Page Link</div>
                <div className="qr-link-wrapper">
                  <div className="qr-link-text">{qrCodeData.url}</div>
                  <button className="qr-copy-btn" onClick={handleCopyLink} title="Copy link">
                    <IconCopy size={18} stroke={2} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Vouchers Modal */}
      {showCampaignVouchersModal && selectedCampaignForVouchers && (
        <div className="modal-overlay" onClick={() => setShowCampaignVouchersModal(false)}>
          <div className="modal-content modal-voucher" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Campaign Vouchers - {selectedCampaignForVouchers.name}</h3>
              <button className="modal-close" onClick={() => setShowCampaignVouchersModal(false)}>
                <IconX size={20} stroke={2} />
              </button>
            </div>
            {!campaignVouchersLoading && campaignVouchers.length > 0 && (
              <p className="campaign-vouchers-modal-count">
                {campaignVouchers.length} active voucher{campaignVouchers.length === 1 ? '' : 's'} (not expired)
              </p>
            )}
            <div className="modal-body">
              {campaignVouchersLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>Loading vouchers...</div>
              ) : campaignVouchers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>No vouchers found for this campaign.</div>
              ) : (
                <div className="voucher-select-table">
                  <div className="voucher-select-header campaign-vouchers-header">
                    <span>Voucher</span>
                    <span>Voucher Type</span>
                    <span>Price</span>
                    <span>Brand</span>
                    <span>Quantity</span>
                    <span>Valid Period</span>
                    <span>Category</span>
                    <span>Country</span>
                  </div>
                  {campaignVouchers.map((voucher) => (
                    <div key={voucher.id} className="voucher-select-row-multi campaign-vouchers-row">
                      <span>{voucher.name}</span>
                      <span>{voucher.type}</span>
                      <span>{voucher.price}</span>
                      <span>{voucher.brand}</span>
                      <span>{voucher.quantity}</span>
                      <span>{voucher.validPeriod}</span>
                      <span>{voucher.category}</span>
                      <span>{voucher.country}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowCampaignVouchersModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarketingPages;
